import type { Client, ClientHeaders } from "@medusajs/js-sdk";

import type { StoreReorderLine } from "./reorder";

/**
 * Client for the opt-in **salon stock-tracking** store endpoints
 * (`/store/stock-tracking`, api#148).
 *
 * A salon opts a *variant* (supplier-agnostic, a physical shelf count) into
 * tracking and maintains a `reorder_level` + optional `on_hand`. The owner is
 * always the authenticated salon - never sent in a body - so a tracked item's
 * identity on the wire is its `variant_id`, and the upsert/untrack calls key on
 * it.
 *
 * api#148 gives us CRUD + the cold-start seed (`candidates` + `bulk`); api#152
 * adds the reorder path (`previewReorder` / `confirmReorder`), which resolves a
 * bare tracked variant to a concrete supplier offer server-side so it can reach
 * the offer-strict seller cart. No `@salonstocker/framework` wire types exist for
 * this module yet, so the request/response shapes are defined here (as
 * `saved-list.ts` does for its own response wrappers).
 */

/** Variant display fields the routes hydrate for the "My Stock" table. */
export interface StoreStockTrackingVariant {
  id: string;
  title: string | null;
  sku: string | null;
  product: {
    id: string;
    title: string | null;
    thumbnail: string | null;
    handle: string | null;
  } | null;
}

/**
 * A tracked item as returned on the wire: the stored row + the server-computed
 * `is_due` predicate + the hydrated variant (`null` when the variant is no
 * longer resolvable, e.g. deleted from the catalog).
 */
export interface StoreStockTrackingItem {
  id: string;
  variant_id: string;
  reorder_level: number;
  /** `null` = "tracking, not counted yet" - render distinctly from `0`. */
  on_hand: number | null;
  /** Read-time predicate: `on_hand` set AND `on_hand <= reorder_level`. */
  is_due: boolean;
  created_at: string;
  updated_at: string;
  variant: StoreStockTrackingVariant | null;
}

/**
 * A seed candidate: a distinct, currently-purchasable variant from the salon's
 * own order history that they do not already track (the server does the
 * exclusion).
 */
export interface StoreStockTrackingCandidate {
  variant_id: string;
  variant: StoreStockTrackingVariant | null;
}

/**
 * The mutable fields of a tracked item. `reorder_level` defaults to `1`
 * server-side; an omitted `on_hand` stores `null` ("tracking, not counted yet")
 * - the upsert is **full-state**, so leaving `on_hand` out resets it to null.
 */
export interface StoreUpsertStockTracking {
  variant_id: string;
  reorder_level?: number;
  on_hand?: number | null;
}

/** Body of the cold-start seed: at least one item, same per-item shape. */
export interface StoreBulkUpsertStockTracking {
  items: StoreUpsertStockTracking[];
}

/** Optional filter + pagination for the tracked-items index. */
export interface StoreStockTrackingListQuery {
  /** When true, return only items that are due to reorder. */
  due?: boolean;
  offset?: number;
  limit?: number;
}

/** Optional pagination for the "track from your orders" candidate picker. */
export interface StoreStockTrackingCandidatesListQuery {
  offset?: number;
  limit?: number;
}

/**
 * The tracked-items index. `due_count` is the number of due items across the
 * *whole* tracked list (for the home hero stat), independent of the `due`
 * filter; `count` reflects the current (filtered) selection.
 */
export interface StoreStockTrackingListResponse {
  stock_items: StoreStockTrackingItem[];
  count: number;
  due_count: number;
  offset: number;
  limit: number;
}

export interface StoreStockTrackingItemResponse {
  stock_item: StoreStockTrackingItem;
}

export interface StoreStockTrackingBulkResponse {
  stock_items: StoreStockTrackingItem[];
  count: number;
}

export interface StoreUntrackStockTrackingResponse {
  variant_id: string;
  object: string;
  deleted: boolean;
}

export interface StoreStockTrackingCandidatesResponse {
  candidates: StoreStockTrackingCandidate[];
  count: number;
  offset: number;
  limit: number;
}

/**
 * One resolved line of a variant-keyed reorder (api#152). The changed-line shape
 * is exactly `/store/reorder`'s {@link StoreReorderLine} (`available` /
 * `repriced` old->new / `unavailable`, prices in major units), plus the resolved
 * supplier: a tracked item is a bare, supplier-agnostic variant, so the server
 * picks the offer (last-ordered seller for that variant, else cheapest active
 * in-stock) and reports it here. Because different variants can resolve to
 * different suppliers, the seller is **per line**, not per response, and one
 * reorder can fan out across several seller carts. `seller` is `null` on an
 * `unavailable` line (no active offer to resolve).
 */
export interface StoreStockReorderLine extends StoreReorderLine {
  seller: { id: string; name: string } | null;
}

export interface StoreStockReorderRequest {
  /** The tracked variants to reorder (a single id for one item, or all due). */
  variant_ids: string[];
  /**
   * Preview only (`true`) or add the resolved lines to the seller cart(s)
   * (`false`). Defaults to `true` server-side, mirroring `/store/reorder`.
   */
  dry_run?: boolean;
}

export interface StoreStockReorderResponse {
  /**
   * The seller carts the lines were added to. Always present: empty on a
   * dry-run preview (and when nothing was addable), one id per seller after a
   * confirm - plural because a variant-keyed reorder can fan out across
   * suppliers.
   */
  cart_ids: string[];
  /**
   * ISO currency (e.g. `"aud"`) the line prices are quoted in - the pricing
   * currency the endpoint resolved (the buyer's active cart, else the store's
   * default region). Amounts in `lines` are in this currency's major units.
   * Preferred when present so the currency travels with the amounts rather than
   * being re-derived from region state (which could drift from what was priced);
   * the storefront falls back to the active region's currency when it is absent.
   */
  currency_code?: string;
  lines: StoreStockReorderLine[];
}

export class StockTracking {
  private client: Client;

  constructor(client: Client) {
    this.client = client;
  }

  /**
   * The salon's tracked items, hydrated with variant display fields. Always
   * carries `due_count` (whole-list) for the hero stat; pass `due: true` to
   * narrow the returned rows to the due-to-reorder set.
   */
  list = async (
    query?: StoreStockTrackingListQuery,
    headers?: ClientHeaders
  ): Promise<StoreStockTrackingListResponse> =>
    this.client.fetch<StoreStockTrackingListResponse>("/store/stock-tracking", {
      method: "GET",
      query,
      headers
    });

  /**
   * Track a variant, or update its level/count. Keyed on the variant, so a
   * second call for the same variant updates the row rather than duplicating it.
   * Full-state: omit `on_hand` to reset it to null.
   */
  upsert = async (
    body: StoreUpsertStockTracking,
    headers?: ClientHeaders
  ): Promise<StoreStockTrackingItemResponse> =>
    this.client.fetch<StoreStockTrackingItemResponse>("/store/stock-tracking", {
      method: "POST",
      body,
      headers
    });

  /** Untrack a variant. A no-op (`deleted: false`) if it was not tracked. */
  untrack = async (
    variantId: string,
    headers?: ClientHeaders
  ): Promise<StoreUntrackStockTrackingResponse> =>
    this.client.fetch<StoreUntrackStockTrackingResponse>(
      `/store/stock-tracking/${variantId}`,
      { method: "DELETE", headers }
    );

  /**
   * Bulk-track variants in one call - the cold-start seed. Validated
   * atomically server-side: if any variant is not purchasable the whole call is
   * rejected, so a seed never half-applies.
   */
  bulk = async (
    items: StoreUpsertStockTracking[],
    headers?: ClientHeaders
  ): Promise<StoreStockTrackingBulkResponse> =>
    this.client.fetch<StoreStockTrackingBulkResponse>(
      "/store/stock-tracking/bulk",
      {
        method: "POST",
        body: { items } satisfies StoreBulkUpsertStockTracking,
        headers
      }
    );

  /**
   * Seed candidates: the distinct, currently-purchasable variants from the
   * salon's order history that they do not already track (server-excluded).
   */
  candidates = async (
    query?: StoreStockTrackingCandidatesListQuery,
    headers?: ClientHeaders
  ): Promise<StoreStockTrackingCandidatesResponse> =>
    this.client.fetch<StoreStockTrackingCandidatesResponse>(
      "/store/stock-tracking/candidates",
      { method: "GET", query, headers }
    );

  /**
   * Resolve tracked variants against live offers WITHOUT touching any cart
   * (`dry_run: true`) - drives the review-before-confirm step and the inline
   * "from <supplier> - $X" the "Due to Reorder" surfaces show. Powers both the
   * single-item reorder (one id) and the hero "Quick Reorder" (all due ids).
   */
  previewReorder = async (
    variantIds: string[],
    headers?: ClientHeaders
  ): Promise<StoreStockReorderResponse> =>
    this.reorderRequest({ variant_ids: variantIds, dry_run: true }, headers);

  /**
   * Re-resolve the given tracked variants and add their available + repriced
   * lines (qty 1 each) to the appropriate seller cart(s), returning the affected
   * `cart_ids`. Re-resolving on confirm (not trusting the preview) is the same
   * anti-stale-preview guarantee as `/store/reorder` (ADR-0002).
   */
  confirmReorder = async (
    variantIds: string[],
    headers?: ClientHeaders
  ): Promise<StoreStockReorderResponse> =>
    this.reorderRequest({ variant_ids: variantIds, dry_run: false }, headers);

  private reorderRequest = async (
    body: StoreStockReorderRequest,
    headers?: ClientHeaders
  ): Promise<StoreStockReorderResponse> =>
    this.client.fetch<StoreStockReorderResponse>(
      "/store/stock-tracking/reorder",
      { method: "POST", body, headers }
    );
}
